import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, QrCode, ClipboardCheck, Search, History, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { REGISTERS, NON_PHYSICAL, KIND_MODULE } from "../lib/registers";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FormDialog } from "./FormDialog";
import { RagBadge } from "./Badges";
import { AssetHistory } from "./AssetHistory";
import { BrandedLabel } from "./BrandedLabel";

export const RegisterTable = ({ kind }) => {
  const cfg = REGISTERS[kind];
  const { can } = useAuth();
  const isManager = can(KIND_MODULE[kind], "manage");
  const canWrite = can(KIND_MODULE[kind], "check");
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [edit, setEdit] = useState(null);
  const [qr, setQr] = useState(null);
  const [hist, setHist] = useState(null);
  const [areas, setAreas] = useState([]);
  const physical = !NON_PHYSICAL.includes(kind);

  const load = useCallback(() => api.get("/assets", { params: { kind } }).then((r) => setRows(r.data)), [kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (physical) api.get("/areas").then((r) => setAreas(r.data)).catch(() => {});
  }, [physical]);
  const areaName = Object.fromEntries(areas.map((a) => [a.id, a.name]));
  const fields = physical
    ? [...cfg.fields, { name: "area_id", label: "Area (for pathway audits)", type: "select", options: areas.map((a) => [a.id, a.name]) }, { name: "route_order", label: "Order within area on walk route", type: "number" }]
    : cfg.fields;

  const save = async (vals) => {
    try {
      if (vals.id) await api.put(`/assets/${vals.id}`, vals);
      else await api.post("/assets", { ...vals, kind });
      toast.success(`${cfg.singular} saved`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      throw e;
    }
  };

  const sections = [...new Set(rows.map((r) => r.section).filter(Boolean))].sort();
  const shown = rows.filter((r) => (!section || r.section === section) && (!q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4" data-testid={`register-${kind}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder={`Search ${cfg.title.toLowerCase()}...`} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`register-${kind}-search`} />
        </div>
        {physical && rows.length > 0 && (
          <Button variant="outline" onClick={() => nav(`/asset-labels?kind=${kind}`)} className="tap border-slate-700 bg-transparent" data-testid={`register-${kind}-print-labels`}>
            <Printer className="mr-1 h-4 w-4" /> Print all labels
          </Button>
        )}
        {isManager && (
          <Button onClick={() => setEdit({})} className="tap bg-rose-600 hover:bg-rose-700" data-testid={`register-${kind}-add`}>
            <Plus className="mr-1 h-4 w-4" /> Add {cfg.singular}
          </Button>
        )}
      </div>
      {sections.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`register-${kind}-sections`}>
          {["", ...sections].map((s) => (
            <button key={s || "all"} onClick={() => setSection(s)} data-testid={`section-filter-${s || "all"}`}
              className={`h-9 rounded-md border px-3 text-xs font-semibold uppercase tracking-wide transition-colors ${section === s ? "border-rose-500 bg-rose-600 text-white" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>
              {s || "All sections"} <span className="ml-1 opacity-70">{s ? rows.filter((r) => r.section === s).length : rows.length}</span>
            </button>
          ))}
        </div>
      )}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left">
            <tr>
              {cfg.columns.map(([k, l]) => <th key={k} className="eyebrow px-3 py-3 font-normal">{l}</th>)}
              {physical && <th className="eyebrow px-3 py-3 font-normal">Area</th>}
              <th className="eyebrow px-3 py-3 font-normal">Status</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/70 hover:bg-slate-800/40 transition-colors" data-testid={`register-row-${r.asset_code}`}>
                {cfg.columns.map(([k, , t]) => (
                  <td key={k} className={`px-3 py-3 ${k === "asset_code" ? "font-mono text-xs text-cyan-300" : "text-slate-200"}`}>{t === "date" ? fmtDate(r[k]) : String(r[k] ?? "-")}</td>
                ))}
                {physical && <td className="px-3 py-3 text-xs text-slate-300">{areaName[r.area_id] || "-"}</td>}
                <td className="px-3 py-3"><RagBadge status={r.rag} testid={`rag-${r.asset_code}`} /></td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {canWrite && (cfg.templates || []).map((t) => (
                      <Button key={t} size="sm" variant="outline" className="h-9 border-slate-700 bg-transparent text-xs" onClick={() => nav(`/inspect/${t}?asset=${r.id}`)} data-testid={`inspect-${t}-${r.asset_code}`}>
                        <ClipboardCheck className="mr-1 h-3.5 w-3.5" />{t.replace(/_/g, " ").replace("6monthly", "6-mo")}
                      </Button>
                    ))}
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setHist(r)} data-testid={`history-${r.asset_code}`}><History className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setQr(r)} data-testid={`qr-${r.asset_code}`}><QrCode className="h-4 w-4" /></Button>
                    {isManager && <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEdit(r)} data-testid={`edit-${r.asset_code}`}><Pencil className="h-4 w-4" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={cfg.columns.length + 3} className="px-3 py-10 text-center text-slate-500">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? `Edit ${cfg.singular}` : `New ${cfg.singular}`} fields={fields} initial={edit} onSubmit={save} testid={`${kind}-form`} />
      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="border-slate-700 bg-ink-2 sm:max-w-sm" data-testid="qr-label-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl uppercase">Asset QR label</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-full max-w-[300px]"><BrandedLabel title={qr.name} code={qr.asset_code} sub={qr.location} qrValue={qr.asset_code} footer="Scan me to inspect or report a fault" testid="qr-label-preview" /></div>
              <Button variant="outline" onClick={() => nav(`/asset-labels?kind=${kind}&id=${qr.id}`)} data-testid="qr-print-btn"><Printer className="mr-1 h-4 w-4" />Print label</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AssetHistory asset={hist} onClose={() => setHist(null)} />
    </div>
  );
};
